import { useEffect, useState } from 'react';
import dayjs from 'dayjs';

const SECOND = 1000;
const MINUTE = SECOND * 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;

/**
 * Milliseconds left until `untilMs`. Always derived from the current time so a
 * countdown stays correct across timer throttling in background tabs, where a
 * tick-by-tick subtraction would silently fall behind real time.
 */
export function getRemainingMs(untilMs: number, now: number = Date.now()) {
  return dayjs(untilMs).diff(dayjs(now));
}

export function useCountdown(until: Date) {
  const untilMs = until.getTime();
  const [ms, setMs] = useState(() => getRemainingMs(untilMs));

  const isActive = ms >= SECOND;

  useEffect(() => {
    setMs(getRemainingMs(untilMs));
  }, [untilMs]);

  useEffect(() => {
    if (isActive) {
      const interval = setInterval(() => setMs(getRemainingMs(untilMs)), SECOND);
      return () => {
        clearInterval(interval);
      };
    }
  }, [isActive, untilMs]);

  return { ms, isActive };
}

export function parseMs(ms: number) {
  const days = Math.floor(ms / DAY);
  const hours = Math.floor((ms % DAY) / HOUR);
  const minutes = Math.floor((ms % HOUR) / MINUTE);
  const seconds = Math.floor((ms % MINUTE) / SECOND);

  const result = [days, hours, minutes, seconds] as const;

  return result;
}

export function formatTimeValue(value: number) {
  return value < 10 ? value.toString().padStart(2, '0') : value.toString();
}
